import { HttpError } from '@/lib/httpError'

/**
 * License verification response from API
 */
export interface LicenseVerificationResponse {
  IsValid: boolean
  Product: string
  LicenseType: string
  CustomerName: string
  Email: string
  Organization: string
  ExpiresAt: string
  MaxFlows: number
  ErrorMessage: string
  DaysUntilExpiration: number
  /**
   * True when the signature and payload are genuine but the validity window has
   * passed or not yet started. Such a key can still be applied on purpose; a key
   * that fails for any other reason cannot.
   */
  OutsideValidityWindow: boolean
}

/**
 * One license to mint. Only the vendor installation can act on this: a customer
 * install has no signing key and answers 501, which is the point of the design
 * rather than a configuration to switch on.
 */
export interface IssueLicenseRequest {
  customer_name: string
  email: string
  organization?: string
  customer_id?: string
  license_type?: 'trial' | 'standard' | 'enterprise'
  valid_days: number
  max_flows?: number
  replaces_jti?: string
  contact_name?: string
  contact_email?: string
  // The customer's license request this answers (BAFTT-12).
  request_id?: string
}

/**
 * A minted license. license_key is the only copy: the server records the
 * issuance in its ledger but deliberately does not keep the key itself, so it
 * cannot be fetched again later.
 */
export interface IssuedLicense {
  license_key: string
  jti: string
  /** The code a customer quotes, e.g. SYN-2026-0041. Allocated by the ledger. */
  reference?: string
  product: string
  license_type: string
  customer_name: string
  email: string
  organization?: string
  customer_id?: string
  max_flows: number
  issued_at: string
  expires_at: string
}

/**
 * What the issuance call returns: the license, and whether this installation can
 * also email it. Delivery is configured separately from signing, so an install
 * can be able to issue and not able to send.
 */
export interface IssueLicenseResult {
  license: IssuedLicense
  can_send: boolean
  // False when a request was named but could not be closed; the key is still
  // good and the request stays open for the vendor to close.
  request_closed?: boolean
}

/** One row of the issuance ledger. Carries no key. */
export interface IssuedLicenseRecord {
  jti: string
  reference?: string
  contact_name?: string
  contact_email?: string
  product: string
  license_type: string
  customer_name: string
  customer_email: string
  organization?: string
  customer_id?: string
  max_flows: number
  issued_by: string
  issued_at: string
  expires_at: string
  replaces_jti?: string
  sent_to?: string
  sent_at?: string
  send_error?: string
}

/**
 * License API Service
 * Handles license verification and related operations
 */
// Reads the server's error and code, so a caller can tell "enter a code" apart
// from every other refusal.
async function issuanceError(response: Response, fallback: string): Promise<HttpError> {
  let message = ''
  let code: string | undefined
  try {
    const body = await response.json()
    message = body?.error || body?.message || ''
    code = body?.code || undefined
  } catch {
    message = ''
  }
  return new HttpError(message || fallback, response.status, code)
}

/** What a customer asks the vendor for (BAFTT-12). */
export interface LicenseRequestPayload {
  kind: 'new' | 'renewal'
  customer_name: string
  email: string
  organization?: string
  license_type: 'trial' | 'standard' | 'enterprise'
  valid_days: number
  max_flows?: number
  note?: string
}

/** A filed license request, as the vendor reads it. */
export interface LicenseRequest {
  id: string
  requestor_id: string
  status: 'pending' | 'approved' | 'rejected' | 'cancelled'
  payload: string
  reason?: string
  created_at: string
  processed_at?: string
  requestor?: { id: string; account: string; name: string }
  approver?: { id: string; account: string; name: string }
}

/** Parses a request's payload; null when it is not a license request's. */
export function parseLicenseRequestPayload(payload: string): LicenseRequestPayload | null {
  try {
    const p = JSON.parse(payload)
    return p && typeof p === 'object' && p.license_type ? (p as LicenseRequestPayload) : null
  } catch {
    return null
  }
}

/** Whether this account may use license issuance, and if not, why not. */
export type IssuanceAccess = 'allowed' | 'needs-totp' | 'refused'

class LicenseApiService {
  private baseUrl = '/api/v2/licenses'

  /**
   * Verify a license key
   * @param licenseKey - The license key to verify
   * @returns Verification response
   * @throws HttpError if verification fails
   */
  async verifyLicense(licenseKey: string): Promise<LicenseVerificationResponse> {
    const response = await fetch(`${this.baseUrl}/verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ license: licenseKey }),
    })

    if (!response.ok) {
      // Surface the server's `error` field instead of the raw JSON body so the
      // UI never shows `{"error":"..."}`.
      let message = ''
      try {
        const body = await response.json()
        message = body?.error || body?.message || ''
      } catch {
        message = ''
      }
      throw new HttpError(message || 'Failed to verify license', response.status)
    }

    const data: LicenseVerificationResponse = await response.json()

    // An expired or not-yet-valid key is returned rather than thrown: the caller
    // shows what it is and lets an operator apply it deliberately. Every other
    // failure - a bad signature, a mangled key, one already used - still throws,
    // because there is nothing there worth showing and nothing worth applying.
    if (!data.IsValid && !data.OutsideValidityWindow) {
      throw new Error(data.ErrorMessage || 'License is not valid')
    }

    return data
  }

  /**
   * Mint a new license key.
   *
   * Returns null when this installation cannot issue at all (HTTP 501, no
   * signing key). That is the expected answer on a customer install, so the
   * caller hides the feature rather than reporting a failure.
   */
  async issueLicense(req: IssueLicenseRequest): Promise<IssueLicenseResult | null> {
    const response = await fetch(`${this.baseUrl}/issuance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(req),
    })

    if (response.status === 501) return null

    if (!response.ok) throw await issuanceError(response, 'Failed to issue license')

    return (await response.json()) as IssueLicenseResult
  }

  /**
   * Email a license that was just issued, with the key as a JSON attachment.
   *
   * The key is passed back up because the server never kept it. The server
   * checks that it verifies and that it is the license the jti names, so this
   * cannot be used to mail anything else.
   */
  async sendLicense(jti: string, licenseKey: string, to?: string): Promise<{ sent_to: string }> {
    const response = await fetch(`${this.baseUrl}/issuance/${encodeURIComponent(jti)}/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ license_key: licenseKey, to: to || undefined }),
    })

    if (!response.ok) throw await issuanceError(response, 'Failed to send the license')

    return (await response.json()) as { sent_to: string }
  }

  /**
   * Confirm a code from the authenticator app for this session. Minting and
   * mailing are refused with STEP_UP_REQUIRED until this has succeeded within
   * the last few minutes.
   */
  async confirmIssuance(code: string): Promise<{ confirmed_until: string }> {
    const response = await fetch(`${this.baseUrl}/issuance/confirmation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code }),
    })

    if (!response.ok) throw await issuanceError(response, 'Failed to confirm the code')

    return (await response.json()) as { confirmed_until: string }
  }

  /** File a request for a license with the vendor. */
  async requestLicense(payload: LicenseRequestPayload): Promise<LicenseRequest> {
    const response = await fetch(`${this.baseUrl}/requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    })
    if (!response.ok) throw await issuanceError(response, 'Failed to send the request')
    return (await response.json()) as LicenseRequest
  }

  /**
   * The license issued for a license request. Answers for the customer who
   * asked and for vendor accounts only.
   */
  async getRequestLicense(requestId: string): Promise<IssuedLicense> {
    const response = await fetch(`/api/v2/requests/${encodeURIComponent(requestId)}/license`, {
      credentials: 'include',
    })
    if (!response.ok) throw await issuanceError(response, 'Failed to read the license')
    return (await response.json()) as IssuedLicense
  }

  async getLicenseRequest(id: string): Promise<LicenseRequest> {
    const response = await fetch(`${this.baseUrl}/issuance/requests/${encodeURIComponent(id)}`, {
      credentials: 'include',
    })
    if (!response.ok) throw await issuanceError(response, 'Failed to read the license request')
    return (await response.json()) as LicenseRequest
  }

  async declineLicenseRequest(id: string, reason: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/issuance/requests/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ reason }),
    })
    if (!response.ok) throw await issuanceError(response, 'Failed to decline the request')
  }

  /**
   * Ask the server whether this account may issue. A vendor account without a
   * second factor is told so, rather than treated like an account that may
   * never issue: the first can fix it from their profile.
   */
  async issuanceAccess(): Promise<IssuanceAccess> {
    const response = await fetch(`${this.baseUrl}/issuance?limit=1`, {
      credentials: 'include',
    })
    if (response.ok) return 'allowed'
    if (response.status === 403) {
      const err = await issuanceError(response, '')
      if (err.code === 'TOTP_REQUIRED_FOR_ISSUANCE') return 'needs-totp'
    }
    return 'refused'
  }

  /**
   * Read the issuance ledger. Pass an email to see everything one customer
   * already holds, which is the question support actually asks.
   */
  async listIssued(email?: string): Promise<IssuedLicenseRecord[] | null> {
    const query = email ? `?email=${encodeURIComponent(email)}` : ''
    const response = await fetch(`${this.baseUrl}/issuance${query}`, {
      credentials: 'include',
    })

    if (response.status === 501 || response.status === 403) return null
    if (!response.ok) throw new HttpError('Failed to read the issuance ledger', response.status)

    const body = await response.json()
    return (body?.issued ?? []) as IssuedLicenseRecord[]
  }
}

export const licenseApiService = new LicenseApiService()
